import userService from "./services/user.service";

const typeNameMap = new Map([
  ["LINUX", "Linux host / Linux firewall"],
  ["WINDOWS", "Windows host firewall"],
  ["APPLICATION", "Allowlist (Passive)"],
  ["PROXY", "Proxy"],
  ["SCRIPT", "Custom script"],
]);

const vendorNameMap = new Map([
  ["fortinet", "Fortinet"],
  ["fortimanager", "FortiManager"],
  ["palo", "Palo Alto"],
  ["firepower", "Cisco Firepower"],
  ["sonicwall", "SonicWall"],
  ["juniper", "Juniper"],
  ["checkpoint", "Checkpoint"],
  ["aws", "Amazon AWS SG"],
  ["azure", "Azure NSG"],
  ["digitalocean", "Digital Ocean"],
  ["pfsense", "PfSense"],
  ["mikrotik", "Mikrotik"],
  ["sophosutm", "Sophos UTM"],
  ["sophossfos", "Sophos SFOS"],
]);

async function createBackend({ name, agentId, config }) {
  const response = await userService.createBackend(
    JSON.stringify({
      Name: name,
      AgentID: agentId,
      Config: config,
    }),
  );

  return response.data;
}

async function deleteBackend(id) {
  await userService.deleteBackend(id);
}

async function createAcl({
  description,
  url,
  category,
  icon,
  clickToGrant,
  enterTicketNumber,
  requireTOTP,
  cgNatOpen,
  hideFromUsers,
  hideFromAdmins,
  backendId,
  ipExclusions,
  grantType,
  config,
  parentId,
  maximumGrantDuration,
  autoBrowseUsers,
}) {
  const response = await userService.createAcl(
    JSON.stringify({
      Description: description,
      URL: url,
      Category: category || "Default",
      Icon: icon,
      ClickToGrant: clickToGrant,
      RequireTicketNumber: enterTicketNumber,
      RequireTOTP: requireTOTP,
      CGNATOpen: cgNatOpen,
      HideFromUsers: hideFromUsers,
      HideFromAdmins: hideFromAdmins,
      MaximumGrantDuration: maximumGrantDuration,
      BackendID: backendId,
      IpExclusions: ipExclusions,
      GrantType: grantType,
      Config: config,
      ParentID: parentId,
      AutoBrowseUsers: autoBrowseUsers,
    }),
  );

  return response.data;
}

async function deleteAcl(id) {
  await userService.deleteAcl(id);
}

async function createApiKey({ name, allowedFrom, scopes }) {
  const response = await userService.createApiKey(
    JSON.stringify({
      Name: name,
      AllowedFrom: allowedFrom,
      Scopes: scopes,
    }),
  );

  return response.data;
}

async function deleteApiKey(id) {
  await userService.deleteApiKey(id);
}

export async function createKnoc({
  type,
  agentId,
  form,
  apiKeyForm,
  allowlistAccessIpRestrictionForm,
  apiKeyOption,
  existingApiKey,
  envVarsForm,
  knocOptionsForm,
  users,
  groups,
}) {
  let backend;
  let backendTwo;
  let aclId;
  let aclIdTwo;
  let apiKey;
  let oldUsers = [];
  let oldGroups = [];

  try {
    const name = form.ApplicationType === "active" ? vendorNameMap.get(form.Vendor) : typeNameMap.get(type);

    let backendConfig = form.BackendConfig;
    let aclConfig = form.ACLConfig;
    switch (type) {
      case "LINUX":
        // Detect backend type from ACLConfig data (FormKit v-model may not sync LinuxBackendType to form)
        if (aclConfig?.pf?.TableName) {
          backendConfig = { pf: {} };
          aclConfig = { pf: aclConfig?.pf || {} };
        } else {
          backendConfig = { ipset: {} };
          aclConfig = { ipset: aclConfig?.ipset || {} };
        }
        break;
      case "WINDOWS":
        backendConfig = { windows_firewall: {} };
        break;
      case "APPLICATION":
        if (form.ApplicationType === "passive" || form.ApplicationType === "passive+") {
          backendConfig = { allowlist: {} };
          aclConfig = { allowlist: {} };

          if (apiKeyOption === "none") {
            if (!allowlistAccessIpRestrictionForm) {
              throw Error("err.allowlistAccessIpRestrictionFormRequired");
            }

            aclConfig.allowlist.AllowAccessByIp = true;
            aclConfig.allowlist.AllowedIpAddresses = allowlistAccessIpRestrictionForm.AllowedFrom;
          }
        }

        if (form.ApplicationType === "active") {
          if (form.Vendor === "fortinet") {
            backendConfig = {
              fortios: {
                Bearer: form.BackendConfig.fortios.Bearer,
                Url: form.BackendConfig.fortios.Url,
                VDOM: form.BackendConfig.fortios.VDOM,
                Prefix: form.BackendConfig.fortios.Prefix,
                Insecure: form.BackendConfig.fortios.Insecure,
              },
            };
          } else if (form.Vendor === "fortimanager") {
            backendConfig = {
              fortimanager: {
                ApiKey: form.BackendConfig.fortimanager.ApiKey,
                Hostname: form.BackendConfig.fortimanager.Hostname,
                Insecure: form.BackendConfig.fortimanager.Insecure,
              },
            };
          } else if (form.Vendor === "palo") {
            backendConfig = {
              panos: {
                ApiKey: form.BackendConfig.panos.ApiKey,
                Hostname: form.BackendConfig.panos.Hostname,
                VSys: form.BackendConfig.panos.VSys,
                DeviceGroup: form.BackendConfig.panos.DeviceGroup,
                Insecure: form.BackendConfig.panos.Insecure,
              },
            };
          } else if (form.Vendor === "firepower") {
            backendConfig = {
              firepower: {
                Hostname: form.BackendConfig.firepower.Hostname,
                Username: form.BackendConfig.firepower.Username,
                Password: form.BackendConfig.firepower.Password,
                Insecure: form.BackendConfig.firepower.Insecure,
              },
            };
          } else if (form.Vendor === "sonicwall") {
            backendConfig = {
              sonicwall: {
                Hostname: form.BackendConfig.sonicwall.Hostname,
                Username: form.BackendConfig.sonicwall.Username,
                Password: form.BackendConfig.sonicwall.Password,
                Insecure: form.BackendConfig.sonicwall.Insecure,
              },
            };
          } else if (form.Vendor === "sophosutm") {
            backendConfig = {
              sophosutm: {
                Hostname: form.BackendConfig.sophosutm.Hostname,
                ApiKey: form.BackendConfig.sophosutm.ApiKey,
                Insecure: form.BackendConfig.sophosutm.Insecure,
              },
            };
          } else if (form.Vendor === "sophossfos") {
            backendConfig = {
              sophossfos: {
                Hostname: form.BackendConfig.sophossfos.Hostname,
                Username: form.BackendConfig.sophossfos.Username,
                Password: form.BackendConfig.sophossfos.Password,
                Insecure: form.BackendConfig.sophossfos.Insecure,
              },
            };
          } else if (form.Vendor === "aws") {
            backendConfig = {
              aws: {
                AccessKeyId: form.BackendConfig.aws.AccessKeyId,
                SecretAccessKey: form.BackendConfig.aws.SecretAccessKey,
                Region: form.BackendConfig.aws.Region,
              },
            };
            aclConfig.aws.Port = parseInt(aclConfig.aws.Port);
          } else if (form.Vendor === "azure") {
            backendConfig = {
              azure: {
                ClientId: form.BackendConfig.azure.ClientId,
                ClientSecret: form.BackendConfig.azure.ClientSecret,
                TenantId: form.BackendConfig.azure.TenantId,
                NoCache: form.BackendConfig.azure.NoCache,
              },
            };
            aclConfig.azure.NsgPort = parseInt(aclConfig.azure.NsgPort);
          } else if (form.Vendor === "scipt") {
            backendConfig = {
              script: {
                Command: `/temp/${form.Vendor}`,
                EnvVars: form.EnvVars.map((envVar) => ({
                  Name: envVar.Name,
                  Sensitive: envVar.Sensitive,
                })),
              },
            };
            aclConfig = {
              script: {
                EnvVars: form.EnvVars.reduce((acc, envVar) => ({ ...acc, [envVar.Name]: { Value: envVar.Value } }), {}),
              },
            };
          } else if (form.Vendor === "cloudflarelist") {
            backendConfig = {
              cloudflarelist: {
                AccountId: form.BackendConfig.cloudflarelist.AccountId,
                ListId: form.BackendConfig.cloudflarelist?.ListId,
                ListName: form.BackendConfig.cloudflarelist?.ListName,
                ApiKey: form.BackendConfig.cloudflarelist.ApiKey,
              },
            };
          } else if (form.Vendor === "digitalocean") {
            backendConfig = {
              digitalocean: {
                PersonalAccessToken: form.BackendConfig.digitalocean.PersonalAccessToken,
              },
            };
          } else {
            throw Error("invalid backend type");
          }
        }

        break;

      case "SCRIPT":
        backendConfig.script.EnvVars = envVarsForm.EnvVars.filter((envVar) => envVar.Name !== "").map((envVar) => ({
          Name: envVar.Name,
          Sensitive: envVar.Sensitive,
        }));
        aclConfig.script.EnvVars = envVarsForm.EnvVars.filter((envVar) => envVar.Name !== "").reduce(
          (acc, envVar) => ({ ...acc, [envVar.Name]: { Value: envVar.Value } }),
          {},
        );
        break;
    }

    // We don't send the agentId here because this is setting up the allowlist, not the actual backend for passive+
    backend = await createBackend({
      name,
      agentId: form.ApplicationType === "passive+" ? null : agentId,
      config: backendConfig,
    });

    if (form.ApplicationType === "passive" || form.ApplicationType === "passive+") {
      if (apiKeyOption === "new" && apiKeyForm) {
        apiKey = await createApiKey({
          name: apiKeyForm.Name,
          allowedFrom: apiKeyForm.AllowedFrom,
          scopes: undefined,
        });
        aclConfig.allowlist.ApiKeyId = apiKey.ID;
      } else if (apiKeyOption === "existing" && existingApiKey) {
        aclConfig.allowlist.ApiKeyId = existingApiKey.ID;
      }
    }

    let aclData = {
      description: form.Description,
      url: form.URL,
      category: form.Category,
      icon: form.Icon,
      clickToGrant: knocOptionsForm.ClickToGrant,
      enterTicketNumber: knocOptionsForm.EnterTicketNumber,
      requireTOTP: knocOptionsForm.RequireTOTP,
      cgNatOpen: form.CGNATOpen,
      hideFromUsers: knocOptionsForm.HideFromUsers,
      maximumGrantDuration: knocOptionsForm.MaximumGrantDuration * 60,
      backendId: backend.ID,
      ipExclusions: knocOptionsForm.IpExclusions,
      grantType: knocOptionsForm.GrantType,
      config: aclConfig,
      autoBrowseUsers: knocOptionsForm.AutoBrowseUsers,
      parentId: null,
      hideFromAdmins: false,
    };

    if (form.ParentID) {
      aclData.parentId = knocOptionsForm.ParentID;
    }

    aclId = await createAcl(aclData);

    if (form.ApplicationType === "passive+") {
      let backendConfig = form.BackendConfig;

      // The formkit object doesn't map perfectly into the request... I hate formkit...
      if (form.Vendor === "palo") {
        backendConfig = {
          panos: {
            ApiKey: form.BackendConfig.panos.ApiKey,
            Hostname: form.BackendConfig.panos.Hostname,
            VSys: form.BackendConfig.panos.VSys,
            DeviceGroup: form.BackendConfig.panos.DeviceGroup,
            Insecure: form.BackendConfig.panos.Insecure,
            MapUserAlternateHost: form.BackendConfig.panos.MapUserAlternateHost,
            MapUserHostname: form.BackendConfig.panos.MapUserHostname,
            MapUserInsecure: form.BackendConfig.panos.MapUserInsecure,
            MapUserApiKey: form.BackendConfig.panos.MapUserApiKey,
            MapUserSerialNumbers: form.BackendConfig.panos.MapUserSerialNumbers,
            MapUserVSys: form.BackendConfig.panos.MapUserVSys,
          },
        };
      }

      backendTwo = await createBackend({
        name: vendorNameMap.get(form.Vendor),
        agentId,
        config: backendConfig,
      });

      // We don't want to assign the second ACL ID to a user, because in parent/child configurations, it is expected that
      // the user is only assigned to the parent ACL.
      aclIdTwo = await createAcl({
        description: form.Description,
        url: form.URL,
        category: form.Category,
        icon: form.Icon,
        clickToGrant: knocOptionsForm.ClickToGrant,
        enterTicketNumber: knocOptionsForm.EnterTicketNumber,
        requireTOTP: knocOptionsForm.RequireTOTP,
        cgNatOpen: form.CGNATOpen,
        hideFromAdmins: true,
        hideFromUsers: knocOptionsForm.HideFromUsers,
        maximumGrantDuration: knocOptionsForm.MaximumGrantDuration * 60,
        backendId: backendTwo.ID,
        config: form.ACLConfig,
        parentId: aclId,
        autoBrowseUsers: false,
        ipExclusions: null,
        grantType: null,
      });
    }

    for (const user of users) {
      await userService.updateUser(
        user.ID,
        JSON.stringify({
          ...user,
          AclIds: user.AclIds ? [...user.AclIds, aclId] : [aclId],
        }),
      );
      oldUsers.push(user);
    }

    for (const group of groups) {
      await userService.updateGroup(
        group.ID,
        JSON.stringify({
          ...group,
          ACLIDs: group.ACLIDs ? [...group.ACLIDs, aclId] : [aclId],
        }),
      );
      oldGroups.push(group);
    }

    let acl;
    if (form.ApplicationType === "passive" || form.ApplicationType === "passive+") {
      const aclResponse = await userService.getAcl(aclId);
      acl = aclResponse.data;
    }

    return [aclId, apiKey, acl];
  } catch (error) {
    for (const group of oldGroups) {
      await userService.updateGroup(group.ID, JSON.stringify(group));
    }

    for (const user of oldUsers) {
      await userService.updateUser(user.ID, JSON.stringify(user));
    }

    if (Boolean(apiKey)) {
      await deleteApiKey(apiKey.ID);
    }

    if (Boolean(aclIdTwo)) {
      await deleteAcl(aclIdTwo);
    }

    if (Boolean(backendTwo)) {
      await deleteBackend(backendTwo.ID);
    }

    if (Boolean(aclId)) {
      await deleteAcl(aclId);
    }

    if (Boolean(backend)) {
      await deleteBackend(backend.ID);
    }

    throw error;
  }
}
